<div class="tpl-container">

  <!-- ── Toolbar ────────────────────────────────────────────────────────────── -->
  <div class="tpl-toolbar">
    <span class="tpl-title">Resource Templates</span>
    <span class="tpl-count mat-body-2" *ngIf="dataSource.data.length">
      {{ dataSource.data.length }} template(s)
    </span>

    <button mat-stroked-button (click)="refreshRequested.emit()" [disabled]="isLoading">
      <mat-icon>refresh</mat-icon> Refresh
    </button>

    <button mat-stroked-button (click)="exportCsv()" [disabled]="!dataSource.data.length">
      <mat-icon>download</mat-icon> Export CSV
    </button>
  </div>

  <!-- ── Loading state ──────────────────────────────────────────────────────── -->
  <div *ngIf="isLoading" class="tpl-state-row">
    <mat-progress-spinner mode="indeterminate" diameter="36"></mat-progress-spinner>
    <span>Loading templates…</span>
  </div>

  <!-- ── Error state ────────────────────────────────────────────────────────── -->
  <div *ngIf="!isLoading && loadError" class="tpl-error-row">
    <mat-icon>error_outline</mat-icon>
    <span>{{ loadError }}</span>
    <button mat-stroked-button (click)="refreshRequested.emit()">Retry</button>
  </div>

  <!-- ── Empty state ────────────────────────────────────────────────────────── -->
  <div *ngIf="!isLoading && !loadError && !dataSource.data.length" class="tpl-empty">
    <mat-icon class="tpl-empty-icon">view_in_ar</mat-icon>
    <p>No resource templates found for this class.</p>
  </div>

  <!-- ── Table ──────────────────────────────────────────────────────────────── -->
  <div class="tpl-table-wrap" *ngIf="!isLoading && !loadError && dataSource.data.length">
    <table mat-table [dataSource]="dataSource" class="tpl-table">

      <!-- Template Name -->
      <ng-container matColumnDef="templateName">
        <th mat-header-cell *matHeaderCellDef>Name</th>
        <td mat-cell *matCellDef="let row" class="truncate-cell" [matTooltip]="row.templateName">{{ row.templateName ??
          '—' }}</td>
      </ng-container>

      <!-- Type badge -->
      <ng-container matColumnDef="type">
        <th mat-header-cell *matHeaderCellDef>Type</th>
        <td mat-cell *matCellDef="let row">
          <span class="tpl-badge" [ngClass]="isVmType(row) ? 'tpl-badge-vm' : 'tpl-badge-acc'">
            {{ row.templateType ?? '—' }}
          </span>
        </td>
      </ng-container>

      <!-- Subscription (vm only; show — for acc) -->
      <ng-container matColumnDef="subscriptionId">
        <th mat-header-cell *matHeaderCellDef>Subscription</th>
        <td mat-cell *matCellDef="let row" class="truncate-cell" [matTooltip]="row.subscriptionId">
          <ng-container *ngIf="isVmType(row)">{{ truncate(row.subscriptionId) }}</ng-container>
          <ng-container *ngIf="!isVmType(row)">—</ng-container>
        </td>
      </ng-container>

      <!-- Resource Group (vm only) -->
      <ng-container matColumnDef="resourceGroup">
        <th mat-header-cell *matHeaderCellDef>Resource Group</th>
        <td mat-cell *matCellDef="let row" class="truncate-cell" [matTooltip]="row.resourceGroup">
          <ng-container *ngIf="isVmType(row)">{{ truncate(row.resourceGroup) }}</ng-container>
          <ng-container *ngIf="!isVmType(row)">—</ng-container>
        </td>
      </ng-container>

      <!-- VM Name / Cloud Username -->
      <ng-container matColumnDef="vmOrCloud">
        <th mat-header-cell *matHeaderCellDef>VM / Account</th>
        <td mat-cell *matCellDef="let row">
          <ng-container *ngIf="isVmType(row)">{{ row.vmName ?? '—' }}</ng-container>
          <ng-container *ngIf="isAccType(row)">{{ row.cloudUsername ?? '—' }}</ng-container>
        </td>
      </ng-container>

      <!-- Snapshot Name / Description -->
      <ng-container matColumnDef="snapshotOrDesc">
        <th mat-header-cell *matHeaderCellDef>Snapshot / Description</th>
        <td mat-cell *matCellDef="let row" class="truncate-cell">
          <ng-container *ngIf="isVmType(row)" [matTooltip]="row.snapshotName">{{ truncate(row.snapshotName)
            }}</ng-container>
          <ng-container *ngIf="isAccType(row)" [matTooltip]="row.description">{{ truncate(row.description)
            }}</ng-container>
        </td>
      </ng-container>

      <!-- IP Address (vm only) -->
      <ng-container matColumnDef="ipAddress">
        <th mat-header-cell *matHeaderCellDef>IP Address</th>
        <td mat-cell *matCellDef="let row">
          <ng-container *ngIf="isVmType(row)">{{ row.publicIPAddress ?? '—' }}</ng-container>
          <ng-container *ngIf="!isVmType(row)">—</ng-container>
        </td>
      </ng-container>

      <!-- Power State (vm type only) -->
      <ng-container matColumnDef="powerState">
        <th mat-header-cell *matHeaderCellDef>State</th>
        <td mat-cell *matCellDef="let row">
          <ng-container *ngIf="isVmType(row)">
            <span class="ps-badge" [ngClass]="powerStateClass(row)" *ngIf="powerStateLabel(row) !== '—'">
              {{ powerStateLabel(row) }}
            </span>
            <span *ngIf="powerStateLabel(row) === '—'" class="ps-badge ps-unknown">—</span>
          </ng-container>
          <ng-container *ngIf="!isVmType(row)">—</ng-container>
        </td>
      </ng-container>

      <!-- Actions -->
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef class="tpl-actions-header">Actions</th>
        <td mat-cell *matCellDef="let row" class="tpl-actions-cell">
          <span
            [matTooltip]="isNotCreated(row) ? 'VM is not provisioned yet' : (isBusy(row) ? 'Action in progress…' : 'Actions')"
            [matTooltipShowDelay]="300">
            <button mat-icon-button [matMenuTriggerFor]="rowMenu" [disabled]="isBusy(row) || isNotCreated(row)">
              <mat-icon>more_vert</mat-icon>
            </button>
          </span>
          <mat-menu #rowMenu="matMenu">

            <!-- VM type: power actions (no Launch) -->
            <ng-container *ngIf="isVmType(row)">
              <button mat-menu-item (click)="startTemplate(row)">
                <mat-icon>play_circle</mat-icon> Start
              </button>
              <button mat-menu-item (click)="shutdownTemplate(row)">
                <mat-icon>power_settings_new</mat-icon> Shutdown
              </button>
              <button mat-menu-item (click)="deallocateTemplate(row)">
                <mat-icon>stop_circle</mat-icon> Deallocate
              </button>
            </ng-container>

            <!-- VM type: destroy (gated) -->
            <ng-container *ngIf="isVmType(row) && canDestroyResources">
              <mat-divider></mat-divider>
              <button mat-menu-item (click)="destroyTemplateVm(row)">
                <mat-icon color="warn">delete_forever</mat-icon>
                <span class="tpl-danger-text">Destroy Template VM</span>
              </button>
            </ng-container>

            <!-- ACC type: delete (gated) -->
            <ng-container *ngIf="isAccType(row) && canDestroyResources">
              <button mat-menu-item (click)="deleteAccTemplate(row)">
                <mat-icon color="warn">delete_forever</mat-icon>
                <span class="tpl-danger-text">Delete Account Template</span>
              </button>
            </ng-container>

            <!-- ACC type with no destroy rights: show disabled placeholder -->
            <ng-container *ngIf="isAccType(row) && !canDestroyResources">
              <button mat-menu-item disabled>
                <mat-icon>lock</mat-icon> No destructive actions
              </button>
            </ng-container>

          </mat-menu>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns;" [class.tpl-row-busy]="isBusy(row)"></tr>
    </table>

    <mat-paginator [pageSizeOptions]="pageSizeOptions" [pageSize]="pageSize" appPersistPageSize="class:templates:list" showFirstLastButtons>
    </mat-paginator>
  </div>

</div>
